/**
 * Bind the store's existing product-page fields (variation color select,
 * add-on text input) to the 3D viewer, per the configured CSS selectors.
 */

import type { ThreeViewMode } from '@printapp/previews-3d';
import type { MokkoningConfig } from './config';

export interface ColorState {
    value: string;
    hex: string;
}

/**
 * Wire the configured color <select>. Returns a getter for the current
 * selection (used when assembling the order's design JSON).
 */
export function bindColorSelect(
    cfg: MokkoningConfig,
    mode: () => ThreeViewMode | undefined,
): () => ColorState {
    const select = document.querySelector<HTMLSelectElement>(cfg.colorBinding.selector);

    const current = (): ColorState => {
        const value = (select?.value ?? '').trim().toLowerCase();
        return { value, hex: cfg.colorBinding.map[value] ?? '' };
    };

    const apply = (): void => {
        const { hex } = current();
        if (!hex) return;
        for (const slot of cfg.colorBinding.slots) {
            try {
                mode()?.setColor(slot, hex);
            } catch (error) {
                console.error(`[mokkoning] setColor ${slot}:`, error);
            }
        }
    };

    if (select) {
        select.addEventListener('change', apply);
        apply();
    } else {
        console.warn(`[mokkoning] color select not found: ${cfg.colorBinding.selector}`);
    }

    return current;
}

/**
 * Wire the configured text input. Returns a getter for the current text.
 */
export function bindTextInput(
    cfg: MokkoningConfig,
    onChange: () => void,
): () => string {
    if (!cfg.text.enabled) {
        return () => '';
    }
    const input = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(cfg.textBinding.selector);
    if (input) {
        input.addEventListener('input', onChange);
    } else {
        console.warn(`[mokkoning] text input not found: ${cfg.textBinding.selector}`);
    }
    return () => input?.value ?? '';
}
